<template>
  <div>
    <b-card no-body class="mb-1">
      <b-card-body class="d-flex flex-wrap align-items-center" style="gap: 1rem">
        <div class="d-flex align-items-center">
          <span class="mr-50">Сезон</span>
          <b-form-select v-model="season" :options="seasonOptions" style="width: 110px" @change="onSeasonChange" />
        </div>
        <div class="d-flex align-items-center">
          <span class="mr-50">Тур</span>
          <b-form-select v-model="filterTour" :options="tourOptions" style="width: 120px" />
        </div>
        <div class="d-flex align-items-center">
          <span class="mr-50">Группа</span>
          <b-form-select v-model="filterGroup" :options="groupOptions" style="width: 150px" />
        </div>
        <div class="d-flex align-items-center">
          <span class="mr-50">Ведущий</span>
          <b-form-select v-model="filterHost" :options="hostOptions" style="width: 180px" />
        </div>
        <b-button v-if="$can('manage', 'fnf-league')" variant="primary" :disabled="isSyncing" @click="syncFromPlanfix">
          <b-spinner v-if="isSyncing" small class="mr-50" />
          Загрузить из ПФ
        </b-button>
        <!-- Копирует назначенные (с датой-временем) игры с учётом фильтров:
             «13.09 12:30: Дед (Игрок1, Игрок2)» -->
        <b-button variant="outline-primary" @click="copyAssignedGames">
          <feather-icon icon="CopyIcon" class="mr-50" />
          Скопировать назначенные
        </b-button>
        <!-- Расписание предстоящего тура картинкой — по кнопке на группу (при выбранной
             группе — одна). Тур из фильтра, иначе первый тур группы, где есть игры без чата -->
        <b-button
          v-for="tourExport in tourExports"
          :key="tourExport.groupLetter"
          variant="outline-primary"
          :disabled="exportingTourGroup !== null"
          @click="saveTourImage(tourExport)"
        >
          <b-spinner v-if="exportingTourGroup === tourExport.groupLetter" small class="mr-50" />
          <feather-icon v-else icon="ImageIcon" class="mr-50" />
          Группа {{ tourExport.groupLetter }} · Тур {{ tourExport.tour }}
        </b-button>
      </b-card-body>
    </b-card>

    <b-overlay :show="isLoading">
      <b-card no-body>
        <b-card-body>
          <vue-good-table
            :rows="filteredGames"
            :columns="columns"
            :pagination-options="{ enabled: true, perPage: 25, perPageDropdown: [25, 50, 100] }"
            :sort-options="{ enabled: false }"
            :row-style-class="gameRowClass"
          >
            <template slot="table-row" slot-scope="props">
              <span v-if="props.column.field === 'scheduled_at'">{{ formatMskDateTime(props.row.scheduled_at) }}</span>

              <span v-else-if="props.column.field === 'name'">
                <b-link :to="{ name: 'fnf-league-game-view', params: { id: props.row.id } }">{{ props.row.name }}</b-link>
                <b-link :href="props.row.task_url" target="_blank" class="ml-25">
                  <feather-icon icon="ExternalLinkIcon" />
                </b-link>
              </span>

              <span v-else-if="props.column.field === 'group'">{{ groupLabel(props.row) }}</span>

              <span v-else-if="props.column.field === 'status'">
                <b-badge :variant="props.row.is_finished ? 'light-secondary' : 'light-info'">
                  {{ props.row.is_finished ? 'Завершена' : 'Запланирована' }}
                </b-badge>
              </span>

              <span v-else-if="props.column.field === 'players'">
                <div v-for="player in props.row.players" :key="player.planfix_contact_id" class="text-nowrap">
                  <b-badge :variant="chatStatusVariant(player.chat_status)" class="mr-25">{{ chatStatusLabel(player.chat_status) }}</b-badge>
                  {{ player.name }}
                  <feather-icon
                    v-if="player.has_slot_yes_mark"
                    v-b-tooltip.hover
                    icon="CheckIcon"
                    class="text-success ml-25"
                    title="Отметил «+» хотя бы на одном актуальном слоте"
                  />
                </div>
              </span>

              <span v-else-if="props.column.field === 'results'">
                <!-- key по индексу: не сыгравшие делят последнее место -->
                <div v-for="(result, resultIndex) in props.row.results" :key="resultIndex" class="text-nowrap"
                     :class="{ 'text-muted': result.did_not_play }">
                  {{ result.place }}. {{ result.name }} — {{ result.did_not_play ? 'не играл' : result.points }}
                </div>
                <b-button
                  v-if="props.row.results.length"
                  size="sm"
                  variant="flat-primary"
                  class="mt-25 px-50 py-25"
                  @click="saveGameImage(props.row)"
                >
                  Картинкой
                </b-button>
                <!-- Точечный синк игры: результаты тянутся из ПФ принудительно,
                     даже если задача ещё не переведена в «Завершённая» -->
                <b-button
                  v-if="$can('manage', 'fnf-league')"
                  size="sm"
                  variant="flat-secondary"
                  class="mt-25 px-50 py-25"
                  :disabled="gameSyncingId === props.row.id"
                  @click="syncGame(props.row)"
                >
                  <b-spinner v-if="gameSyncingId === props.row.id" small />
                  <span v-else>
                    <feather-icon icon="RefreshCwIcon" class="mr-25" />
                    Синк из ПФ
                  </span>
                </b-button>
              </span>

              <span v-else-if="props.column.field === 'host'">
                <div v-if="props.row.host_name" class="mb-25">
                  <b-badge variant="light-info">{{ props.row.host_name }}</b-badge>
                </div>
                <b-button
                  v-if="$can('manage', 'fnf-league')"
                  size="sm"
                  variant="outline-primary"
                  @click="openSlotModal(props.row)"
                >
                  <feather-icon icon="CalendarIcon" class="mr-25" />
                  {{ props.row.host_name ? 'Сменить слот' : 'Выбрать слот' }}
                </b-button>
                <b-button
                  v-if="$can('manage', 'fnf-league') && (props.row.host_name || props.row.scheduled_at)"
                  size="sm"
                  variant="flat-danger"
                  class="mt-25 px-50 py-25"
                  :disabled="unassigningId === props.row.id"
                  @click="unassignSlot(props.row)"
                >
                  <b-spinner v-if="unassigningId === props.row.id" small />
                  <span v-else>
                    <feather-icon icon="XIcon" class="mr-25" />
                    Отменить слот
                  </span>
                </b-button>
              </span>

              <span v-else-if="props.column.field === 'chat'">
                <b-link v-if="props.row.chat_invite_link" :href="props.row.chat_invite_link" target="_blank">Чат</b-link>
                <b-button
                  v-else-if="$can('manage', 'fnf-league')"
                  size="sm"
                  variant="outline-primary"
                  :disabled="chatCreatingId === props.row.id"
                  @click="createChat(props.row)"
                >
                  <b-spinner v-if="chatCreatingId === props.row.id" small />
                  <span v-else>Создать чат</span>
                </b-button>
              </span>

              <span v-else>{{ props.formattedRow[props.column.field] }}</span>
            </template>
          </vue-good-table>
        </b-card-body>
      </b-card>
    </b-overlay>

    <!-- Итог создания чата: кого добавили, кому отправили ссылку, кого добавить руками -->
    <b-modal v-model="isChatModalVisible" title="Создание чата" ok-only ok-title="Понятно" size="lg">
      <div v-if="chatResult">
        <p>
          Инвайт-ссылка:
          <b-link :href="chatResult.invite_link" target="_blank">{{ chatResult.invite_link }}</b-link>
        </p>
        <div v-for="player in chatResult.players" :key="player.name" class="mb-50">
          <b-badge :variant="chatStatusVariant(player.status)" class="mr-50">{{ chatStatusLabel(player.status) }}</b-badge>
          <b-link v-if="player.telegram_link" :href="player.telegram_link" target="_blank">{{ player.name }}</b-link>
          <span v-else>{{ player.name }}</span>
          <small v-if="player.reason" class="text-muted"> — {{ player.reason }}</small>
        </div>
      </div>
    </b-modal>

    <!-- Выбор свободного слота ведущего для игры -->
    <b-modal v-model="isSlotModalVisible" title="Выбор слота ведущего" size="lg" hide-footer>
      <b-overlay :show="isLoadingSlots">
        <div v-if="!isLoadingSlots && !availableSlots.length" class="text-muted">
          Нет свободных слотов
        </div>
        <b-form-radio-group v-else v-model="selectedSlotId" stacked>
          <b-form-radio v-for="slot in availableSlots" :key="slot.id" :value="slot.id" class="mb-50">
            {{ formatMskDateTime(slot.starts_at) }} — {{ slot.host_name }}
          </b-form-radio>
        </b-form-radio-group>
      </b-overlay>

      <b-button
        variant="primary"
        class="mt-1"
        :disabled="!selectedSlotId || isAssigning"
        @click="assignSlot"
      >
        <b-spinner v-if="isAssigning" small class="mr-50" />
        Назначить
      </b-button>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BButton, BSpinner, BOverlay, BLink, BBadge, BFormSelect, BModal, BFormRadioGroup, BFormRadio, VBTooltip,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import { ref, computed, onUnmounted } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import fnfLeagueStoreModule from '@/views/fnf-league/fnfLeagueStoreModule'
import exportLeagueGameImage from '@/views/fnf-league/exportLeagueGameImage'
import exportLeagueTourImage from '@/views/fnf-league/exportLeagueTourImage'

const STORE_MODULE_NAME = 'app-fnf-league'

export default {
  components: {
    BCard, BCardBody, BButton, BSpinner, BOverlay, BLink, BBadge, BFormSelect, BModal, BFormRadioGroup, BFormRadio, VueGoodTable,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  setup(props, { root }) {
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, fnfLeagueStoreModule)
    onUnmounted(() => { if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME) })

    const toast = useToast()
    const showError = title => toast({ component: ToastificationContent, props: { title, icon: 'AlertTriangleIcon', variant: 'danger' } })
    const showSuccess = title => toast({ component: ToastificationContent, props: { title, icon: 'CheckIcon', variant: 'success' } })
    const showWarning = title => toast({ component: ToastificationContent, props: { title, icon: 'AlertTriangleIcon', variant: 'warning' } })

    const isLoading = ref(false)
    const isSyncing = ref(false)
    const chatCreatingId = ref(null)
    const season = ref(null)
    const seasonOptions = ref([])
    const games = ref([])
    const chatResult = ref(null)
    const isChatModalVisible = ref(false)

    const isSlotModalVisible = ref(false)
    const isLoadingSlots = ref(false)
    const isAssigning = ref(false)
    const selectedGame = ref(null)
    const selectedSlotId = ref(null)
    const availableSlots = ref([])

    const columns = [
      { label: 'Дата', field: 'scheduled_at' },
      { label: 'Игра', field: 'name' },
      { label: 'Группа', field: 'group' },
      { label: 'Тур', field: 'tour' },
      // Отличает игры одного тура в одной подгруппе (поле Planfix «Номер игры»)
      { label: '№ игры', field: 'game_number' },
      { label: 'Статус', field: 'status' },
      { label: 'Игроки', field: 'players' },
      { label: 'Результаты', field: 'results' },
      { label: 'Ведущий', field: 'host' },
      { label: 'Чат', field: 'chat' },
    ]

    const loadGames = () => {
      isLoading.value = true
      store.dispatch(`${STORE_MODULE_NAME}/fetchGames`, { season: season.value })
        .then(response => {
          games.value = response.data.games
          season.value = response.data.season
          seasonOptions.value = response.data.seasons.map(value => ({ value, text: `Сезон ${value}` }))
        })
        .catch(() => showError('Ошибка загрузки игр лиги'))
        .finally(() => { isLoading.value = false })
    }

    const syncFromPlanfix = () => {
      isSyncing.value = true
      store.dispatch(`${STORE_MODULE_NAME}/syncGames`, { season: season.value })
        .then(response => {
          const { games_created: created, games_updated: updated, results_synced: results } = response.data
          showSuccess(`Синк: создано ${created}, обновлено ${updated}, результатов ${results}`)
          loadGames()
        })
        .catch(err => showError(err.response?.data?.message || 'Ошибка синхронизации'))
        .finally(() => { isSyncing.value = false })
    }

    const createChat = game => {
      chatCreatingId.value = game.id
      store.dispatch(`${STORE_MODULE_NAME}/createChat`, game.id)
        .then(response => {
          chatResult.value = response.data
          isChatModalVisible.value = true
          loadGames()
        })
        .catch(err => showError(err.response?.data?.message || 'Ошибка создания чата'))
        .finally(() => { chatCreatingId.value = null })
    }

    const gameSyncingId = ref(null)
    const syncGame = game => {
      gameSyncingId.value = game.id
      store.dispatch(`${STORE_MODULE_NAME}/syncGame`, game.id)
        .then(response => {
          const { game: updatedGame, results_synced: resultsSynced } = response.data
          const index = games.value.findIndex(item => item.id === updatedGame.id)
          if (index !== -1) games.value.splice(index, 1, updatedGame)
          showSuccess(resultsSynced ? 'Игра и результаты обновлены' : 'Игра обновлена, результатов в ПФ нет')
        })
        .catch(err => showError(err.response?.data?.message || 'Ошибка синка игры'))
        .finally(() => { gameSyncingId.value = null })
    }

    const chatStatusVariant = status => ({
      added: 'light-success', invited: 'light-warning', failed: 'light-danger',
    }[status] || 'light-secondary')
    const chatStatusLabel = status => ({
      added: 'в чате', invited: 'ссылка в ЛС', failed: 'не добавлен',
    }[status] || '—')

    const groupLabel = game => {
      if (game.stage === 'group') return game.group_letter
      if (game.stage === 'semifinal') return 'Полуфинал'
      if (game.stage === 'final') return 'Финал'
      return '—'
    }

    // Фильтры по загруженному сезону (вся таблица уже на клиенте)
    const filterTour = ref(null)
    const filterGroup = ref(null)
    const filterHost = ref(null)

    const tourOptions = computed(() => [
      { value: null, text: 'Все туры' },
      ...[...new Set(games.value.map(game => game.tour).filter(tour => tour !== null))]
        .sort((a, b) => a - b)
        .map(tour => ({ value: tour, text: `Тур ${tour}` })),
    ])

    const groupOptions = computed(() => [
      { value: null, text: 'Все группы' },
      ...[...new Set(games.value.map(game => groupLabel(game)).filter(label => label !== '—'))]
        .sort((a, b) => a.localeCompare(b, 'ru'))
        .map(label => ({ value: label, text: label })),
    ])

    const hostOptions = computed(() => [
      { value: null, text: 'Все ведущие' },
      ...[...new Set(games.value.map(game => game.host_name).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b, 'ru'))
        .map(name => ({ value: name, text: name })),
    ])

    const filteredGames = computed(() => games.value.filter(game => (
      (filterTour.value === null || game.tour === filterTour.value)
      && (filterGroup.value === null || groupLabel(game) === filterGroup.value)
      && (filterHost.value === null || game.host_name === filterHost.value)
    )))

    // При смене сезона выбранные тур/группа/ведущий могут не существовать — сбрасываем
    const onSeasonChange = () => {
      filterTour.value = null
      filterGroup.value = null
      filterHost.value = null
      loadGames()
    }

    // «13.09 12:30» из ISO-строки бэкенда (уже в МСК) — без new Date(),
    // иначе часовой пояс браузера сдвинул бы время (см. formatMskDateTime)
    const formatGameDateTimeForCopy = value => {
      const match = /^\d{4}-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value || '')
      if (!match) return null
      const [, month, day, hours, minutes] = match

      return `${day}.${month} ${hours}:${minutes}`
    }

    // Все назначенные (с датой-временем) игры с учётом фильтров:
    // «13.09 12:30: Дед (Игрок1, Игрок2)», по строке на игру, хронологически
    const copyAssignedGames = () => {
      const assigned = filteredGames.value
        .filter(game => game.scheduled_at)
        // ISO-строки сортируются лексикографически так же, как хронологически
        .sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at))

      if (!assigned.length) {
        showError('Нет назначенных игр для копирования')
        return
      }

      const text = assigned.map(game => {
        const players = game.players.map(player => player.name).join(', ')

        return `${formatGameDateTimeForCopy(game.scheduled_at)}: ${game.host_name || '—'} (${players})`
      }).join('\n')

      navigator.clipboard.writeText(text).then(() => showSuccess('Список игр скопирован'))
    }

    const saveGameImage = game => {
      exportLeagueGameImage({ game })
        .catch(() => showError('Не удалось сохранить картинку'))
    }

    // Предстоящий тур по группам: выбранный в фильтре, иначе первый по порядку тур
    // группы, где есть игры без чата. Группа из фильтра — одна кнопка, иначе по кнопке на группу
    const tourExports = computed(() => {
      const groupGames = games.value.filter(game => (
        game.stage === 'group'
        && game.tour !== null
        && (filterGroup.value === null || game.group_letter === filterGroup.value)
      ))
      const groupLetters = [...new Set(groupGames.map(game => game.group_letter))].sort((a, b) => a.localeCompare(b, 'ru'))

      return groupLetters.map(groupLetter => {
        const letterGames = groupGames.filter(game => game.group_letter === groupLetter)
        const tour = filterTour.value !== null
          ? filterTour.value
          : (letterGames.filter(game => !game.chat_id).map(game => game.tour).sort((a, b) => a - b)[0] ?? null)

        return { groupLetter, tour, games: letterGames.filter(game => game.tour === tour) }
      }).filter(tourExport => tourExport.games.length)
    })

    const exportingTourGroup = ref(null)
    const saveTourImage = async tourExport => {
      exportingTourGroup.value = tourExport.groupLetter
      try {
        const response = await store.dispatch(`${STORE_MODULE_NAME}/fetchStandings`, { season: season.value })
        const standingsGroup = response.data.groups.find(group => group.group_letter === tourExport.groupLetter)
        await exportLeagueTourImage({
          season: season.value,
          groupLetter: tourExport.groupLetter,
          tour: tourExport.tour,
          games: tourExport.games,
          standingsByContactId: Object.fromEntries((standingsGroup?.rows || []).map(row => [row.contact_id, row])),
        })
      } catch (error) {
        showError('Не удалось сохранить картинку тура')
      } finally {
        exportingTourGroup.value = null
      }
    }

    // «13.09.2026 12:30» по МСК — для колонки «Дата» и модалки слотов.
    // Бэкенд отдаёт даты уже в МСК (toIso8601String() при APP_TIMEZONE=Europe/Moscow) —
    // new Date(value) перевёл бы время в часовой пояс браузера, поэтому берём
    // компоненты даты/времени прямо из строки регуляркой.
    const formatMskDateTime = value => {
      const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value || '')
      if (!match) return '—'
      const [, year, month, day, hours, minutes] = match

      return `${day}.${month}.${year} ${hours}:${minutes}`
    }

    const openSlotModal = game => {
      selectedGame.value = game
      selectedSlotId.value = null
      availableSlots.value = []
      isSlotModalVisible.value = true
      isLoadingSlots.value = true
      store.dispatch(`${STORE_MODULE_NAME}/fetchHostSlots`)
        .then(response => {
          // Неактивные (прошедшие незанятыми) слоты для назначения не предлагаются
          availableSlots.value = response.data.slots.filter(slot => slot.league_game_id === null && slot.is_active)
        })
        .catch(() => showError('Ошибка загрузки слотов'))
        .finally(() => { isLoadingSlots.value = false })
    }

    const assignSlot = () => {
      if (!selectedGame.value || !selectedSlotId.value) return
      isAssigning.value = true
      store.dispatch(`${STORE_MODULE_NAME}/assignHostSlot`, { gameId: selectedGame.value.id, slotId: selectedSlotId.value })
        .then(response => {
          const { game: updatedGame, chat_warning: chatWarning } = response.data
          const index = games.value.findIndex(item => item.id === updatedGame.id)
          if (index !== -1) games.value.splice(index, 1, updatedGame)
          isSlotModalVisible.value = false
          showSuccess('Слот назначен')
          if (chatWarning) showWarning(chatWarning)
        })
        .catch(err => showError(err.response?.data?.message || 'Ошибка назначения слота'))
        .finally(() => { isAssigning.value = false })
    }

    // Отмена назначения: ПФ без даты/ведущего, слот свободен, чату возвращается
    // название без времени — запись игроков снова открыта. Ведущий остаётся в чате.
    const unassigningId = ref(null)
    const unassignSlot = async game => {
      const confirmed = await root.$bvModal.msgBoxConfirm(
        `Отменить слот игры «${game.name}»? Дата и ведущий снимутся в Планфиксе, название чата вернётся, запись игроков снова откроется. Ведущий останется в чате.`,
        { title: 'Отмена слота', okTitle: 'Отменить слот', cancelTitle: 'Назад', okVariant: 'danger', centered: true },
      )
      if (!confirmed) return

      unassigningId.value = game.id
      store.dispatch(`${STORE_MODULE_NAME}/unassignHostSlot`, game.id)
        .then(response => {
          const { game: updatedGame, chat_warning: chatWarning } = response.data
          const index = games.value.findIndex(item => item.id === updatedGame.id)
          if (index !== -1) games.value.splice(index, 1, updatedGame)
          showSuccess('Слот отменён')
          if (chatWarning) showWarning(chatWarning)
        })
        .catch(err => showError(err.response?.data?.message || 'Ошибка отмены слота'))
        .finally(() => { unassigningId.value = null })
    }

    // Неназначенная игра с чатом, где у всего состава есть общий «+» по слоту — можно назначать
    const gameRowClass = game => (
      !game.host_name && game.chat_id && game.has_common_yes_slot ? 'league-game-row-ready' : ''
    )

    loadGames()

    return {
      isLoading, isSyncing, chatCreatingId, gameSyncingId, season, seasonOptions, games, columns,
      chatResult, isChatModalVisible, syncGame,
      filterTour, filterGroup, filterHost, tourOptions, groupOptions, hostOptions, filteredGames,
      onSeasonChange, copyAssignedGames, tourExports, exportingTourGroup, saveTourImage,
      isSlotModalVisible, isLoadingSlots, isAssigning, selectedSlotId, availableSlots,
      loadGames, syncFromPlanfix, createChat, chatStatusVariant, chatStatusLabel, groupLabel, saveGameImage,
      formatMskDateTime, openSlotModal, assignSlot, gameRowClass, unassigningId, unassignSlot,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';

.league-game-row-ready td {
  background-color: rgba(40, 199, 111, 0.15);
}
</style>
